import {
  selectHighestPriorityArtifact,
  type Artifact,
  type LocalizedArtifactContent,
} from '@/enterprise/data/useArtifacts';

const NAV_BAR_PLACEMENT = 'nav-bar';

/**
 * Nav-only qualification keys. `navigationConfigKey` is deliberately not `destination.key`: the
 * destination registry still resolves controlled urls for marketing-message CTAs, while a config
 * key identifies a whole nav item whose presentation lives in `navigationConfigRegistry.ts`.
 */
export const NAVIGATION_CONFIG_KEY = {
  FUNDBOX: 'FUNDBOX',
  EXTENDED_NETWORK_FINANCE: 'EXTENDED_NETWORK_FINANCE',
} as const;

export type NavigationConfigKey = (typeof NAVIGATION_CONFIG_KEY)[keyof typeof NAVIGATION_CONFIG_KEY];

/**
 * The key is the whole item, so no presentation field is required or read. `text`, `icon`, `target`,
 * and `children` may still ride along in the payload, and are ignored.
 */
interface NavigationArtifactContent {
  navigationConfigKey: NavigationConfigKey;
}

/**
 * Selector input. `content` is the resolved locale entry. The property is declared here, not taken
 * from the API `content` field, so that field can be removed without changing this file.
 */
type NavigationCandidate = Omit<Artifact, 'content'> & {
  content?: LocalizedArtifactContent | null;
};

export type NavigationArtifact = NavigationCandidate & { content: NavigationArtifactContent };

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isNavBarNavigationArtifact = (artifact: NavigationCandidate): boolean =>
  artifact.artifactType === 'NAVIGATION' && !!artifact.placements?.includes(NAV_BAR_PLACEMENT);

/**
 * Reads `navigationConfigKey` off loosely typed artifact content.
 *
 * Reason: the locale entry is a union across every artifact type, and only the NAVIGATION
 * member declares this field. The `in` check plus a runtime string guard — matching the other guards
 * in this file — narrows it without asserting the artifact type first.
 */
const readNavigationConfigKey = (content: LocalizedArtifactContent | null | undefined): string | undefined => {
  if (content === null || content === undefined || !('navigationConfigKey' in content)) {
    return undefined;
  }

  const { navigationConfigKey } = content;

  return isNonEmptyString(navigationConfigKey) ? navigationConfigKey : undefined;
};

/** NAVIGATION + nav-bar + the caller's config key. Presentation fields are not required. */
const isNavigationArtifactForKey = (
  artifact: NavigationCandidate,
  configKey: NavigationConfigKey
): artifact is NavigationArtifact =>
  isNavBarNavigationArtifact(artifact) && readNavigationConfigKey(artifact.content) === configKey;

/**
 * The highest-priority nav-bar NAVIGATION artifact qualifying the user for `configKey`.
 *
 * An artifact without a matching key is never selected, whatever its destination. SEA derives the key
 * on the read path for both mapped destinations (`artifacts-builder.ts`), so an artifact saved before
 * the field existed still arrives with one.
 *
 * Each financing product owns one config key, and its nav item's label, icon, route, and children are
 * written in `navigationConfigRegistry.ts` — so the artifact only decides whether the user sees the
 * item, not what it looks like.
 *
 * This is the only way to pick a nav artifact, and `useNavigationArtifact(configKey)` is the only
 * caller. There is deliberately no exported per-product selector and no way to supply your own: a
 * selector passed in from a component is a new function on every render, which is how the side nav
 * came to re-render without end (SUPP-19734).
 *
 * There should only be one nav-bar NAVIGATION artifact per config; the priority check is just a
 * super just-in-case fallback.
 */
export const selectNavigationArtifact = (
  configKey: NavigationConfigKey,
  artifacts: NavigationCandidate[]
): NavigationArtifact | undefined =>
  selectHighestPriorityArtifact(artifacts, (artifact): artifact is NavigationArtifact =>
    isNavigationArtifactForKey(artifact, configKey)
  );
